/**
 * Pure helpers for the calendar layout (rev6 Part 4, 6f). Dates are ISO strings ("2026-11-04")
 * with no time, so nothing here depends on the browser's time zone; "today" comes from the
 * server, in India time.
 */

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const pad = (n) => String(n).padStart(2, '0');
export const isoDate = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const parts = (iso) => iso.split('-').map(Number);
const utc = (iso) => {
    const [y, m, d] = parts(iso);
    return new Date(Date.UTC(y, m - 1, d));
};
const fromUtc = (date) => isoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());

export const addDays = (iso, n) => {
    const d = utc(iso);
    d.setUTCDate(d.getUTCDate() + n);
    return fromUtc(d);
};

/** "2026-11" for any date in November 2026. */
export const monthKey = (iso) => iso.slice(0, 7);
export const monthStart = (key) => `${key}-01`;
export const monthEnd = (key) => {
    const [y, m] = key.split('-').map(Number);
    return isoDate(y, m, new Date(Date.UTC(y, m, 0)).getUTCDate());
};
export const addMonths = (key, n) => {
    const [y, m] = key.split('-').map(Number);
    const total = y * 12 + (m - 1) + n;
    return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}`;
};
export const monthLabel = (key) => {
    const [y, m] = key.split('-').map(Number);
    return `${MONTH_NAMES[m - 1]} ${y}`;
};
export const monthName = (key) => MONTH_NAMES[Number(key.split('-')[1]) - 1];

// Monday = 0
const weekday = (iso) => (utc(iso).getUTCDay() + 6) % 7;

/** The month as weeks of 7 dates, Monday first, padded with the days around it. */
export const monthGrid = (key) => {
    const first = monthStart(key);
    const last = monthEnd(key);
    let day = addDays(first, -weekday(first));
    const end = addDays(last, 6 - weekday(last));
    const weeks = [];
    while (day <= end) {
        const week = [];
        for (let i = 0; i < 7; i += 1) {
            week.push(day);
            day = addDays(day, 1);
        }
        weeks.push(week);
    }
    return weeks;
};

/** "4 November", or "Wednesday 4 November" with the weekday. */
export const dayLabel = (iso, withWeekday = false) => {
    const [, m, d] = parts(iso);
    const name = `${d} ${MONTH_NAMES[m - 1]}`;
    return withWeekday ? `${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][weekday(iso)]} ${name}` : name;
};

/** "4 Nov". */
export const shortDay = (iso) => {
    const [, m, d] = parts(iso);
    return `${d} ${MONTH_NAMES[m - 1].slice(0, 3)}`;
};

/** "Mon 9 Nov". */
export const weekdayDay = (iso) => `${WEEKDAYS[weekday(iso)]} ${shortDay(iso)}`;

/** rows by date: { '2026-11-04': [row, ...] }, in the server's order. */
export const rowsByDay = (rows) => {
    const byDay = {};
    (rows || []).forEach((r) => {
        (byDay[r.date] = byDay[r.date] || []).push(r);
    });
    return byDay;
};

/**
 * One day's cards. With grouping (All tasks), languages of one file on the same day become one
 * card: { key, rows, grouped: true }. keyOf says which rows are the same file.
 */
export const dayCards = (rows, group, keyOf = (r) => r.contentId) => {
    if (!group) return rows.map((r) => ({ key: r.id + (r.upNext ? `:${r.stageKey}` : ''), rows: [r], grouped: false }));
    const cards = [];
    const byKey = new Map();
    rows.forEach((r) => {
        const k = keyOf(r);
        if (!byKey.has(k)) {
            const card = { key: k, rows: [] };
            byKey.set(k, card);
            cards.push(card);
        }
        byKey.get(k).rows.push(r);
    });
    return cards.map((c) => ({ ...c, grouped: c.rows.length > 1 }));
};

const POSTING_WORDS = { NOT_READY: 'Not ready yet', READY: 'Ready to post', POSTED: 'Posted' };

/** The one word AV gets for a language. */
export const postingWordLabel = (word) => POSTING_WORDS[word] || '';

// AV's rows carry only that word, so both kinds of row are read the same way
const isPosted = (r) => (r.word ? r.word === 'POSTED' : r.posted);
const isReady = (r) => (r.word ? r.word !== 'NOT_READY' : r.ready || r.posted);
const isDone = (r) => (r.word ? r.word !== 'NOT_READY' : r.status === 'COMPLETED');

/**
 * What a Posting calendar card says, from its languages on that day: "Posted", "2 of 4 posted",
 * "Ready to post", "2 of 4 ready", "Done · needs Ready to post", "3 of 4 done", or "Not ready · 7 of
 * 11 done" from the day before the posting date, when Final check is due. One language still open
 * says its stage instead. AV gets "Not ready yet", with no stage.
 */
export const postingState = (rows, today) => {
    const total = rows.length;
    const done = rows.filter(isDone).length;
    const posted = rows.filter(isPosted).length;
    const ready = rows.filter(isReady).length;
    if (posted === total) return { kind: 'posted', label: 'Posted' };
    if (posted > 0) return { kind: 'done', label: `${posted} of ${total} posted` };
    if (ready === total) return { kind: 'done', label: 'Ready to post' };
    if (ready > 0) return { kind: 'done', label: `${ready} of ${total} ready` };
    if (rows[0].word) return { kind: 'open', label: postingWordLabel('NOT_READY') };
    if (done === total) return { kind: 'done', label: 'Done · needs Ready to post' };
    const date = rows[0].date;
    // Every language waiting says so, and not "0 of 3 done"
    const waiting = done === 0 && rows.every((r) => r.stageName === rows[0].stageName && r.stageName?.startsWith('Waiting for'));
    const progress = total === 1 || waiting ? rows[0].stageName || 'Open' : `${done} of ${total} done`;
    if (date && today && date <= addDays(today, 1)) return { kind: 'notReady', label: `Not ready · ${progress}` };
    return { kind: 'open', label: progress };
};

/**
 * The same post on the Posting calendar: a post's id could only match a video's by chance, but keep
 * them apart. Also posting on is its own card, as it has its own platform and Posted tick.
 */
export const postingKey = (row) => `${row.contentType}:${row.contentId}:${row.slot || 1}`;

/** Done cards past their date with a language not ticked: the "2 not posted" on Earlier. */
export const notPostedCount = (cards) => cards.filter((c) => c.rows.every(isDone) && !c.rows.every(isPosted)).length;

/** Whether a day is inside a date filter's dates; true when there is none. */
export const insideFilter = (iso, filterFrom, filterTo) =>
    (!filterFrom || iso >= filterFrom) && (!filterTo || iso <= filterTo);

/** "Showing next 2 weeks only" for the bar, from the server's dateFilter value. */
export const filterNote = (label) => (label ? `Showing ${label} only` : '');
